import { useEffect, useState } from "react";
import { Plus, X, Search, GraduationCap, Loader2 } from "lucide-react";
import { Label } from "../ui/label";
import {
  searchOrgTeacherCandidates,
  coteachingErrorMessage,
  type CoTeacher,
} from "./coteaching-api";

type CoTeachingPickerProps = {
  organizationId: string;
  selected: CoTeacher[];
  onChange: (next: CoTeacher[]) => void;
};

// Controlled co-teacher picker for the create flow: the new teacher row does
// not exist yet, so selections are held in parent state and persisted once the
// teacher is created. Searches teachers within the chosen organization.
export const CoTeachingPicker = ({
  organizationId,
  selected,
  onChange,
}: CoTeachingPickerProps) => {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CoTeacher[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    if (!picking) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      setSearching(true);
      setSearchError("");
      searchOrgTeacherCandidates(organizationId, query.trim())
        .then((found) => {
          if (cancelled) return;
          const linked = new Set(selected.map((c) => c.teacher_id));
          setResults(found.filter((t) => !linked.has(t.teacher_id)));
        })
        .catch((e) => {
          if (!cancelled)
            setSearchError(coteachingErrorMessage(e, "Search failed"));
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [picking, query, organizationId, selected]);

  const addCoTeacher = (teacher: CoTeacher) => {
    setPicking(false);
    setQuery("");
    onChange([...selected, teacher]);
  };

  const removeCoTeacher = (teacher: CoTeacher) => {
    onChange(selected.filter((c) => c.teacher_id !== teacher.teacher_id));
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label>Co-teaching with</Label>
        <p className="text-xs text-muted-foreground">
          These teachers can co-teach in this account. One-directional: they
          don't see this teacher's account.
        </p>
      </div>

      {selected.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {selected.map((c) => (
            <div
              key={c.teacher_id}
              className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-lime-600/10 text-xs font-medium text-lime-900">
                  {(c.name ?? c.email ?? "?").charAt(0).toUpperCase()}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {c.name ?? "(no name)"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {c.email}
                  </span>
                </div>
                <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  <GraduationCap className="h-3 w-3" />
                  Teacher
                </span>
              </div>
              <button
                type="button"
                aria-label={`Remove ${c.name ?? c.email}`}
                onClick={() => removeCoTeacher(c)}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No co-teaching links yet.</p>
      )}

      {picking ? (
        <div className="rounded-md border border-border/60 bg-background shadow-sm">
          <div className="flex items-center gap-2 border-b border-border/60 px-2.5">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder="Search teacher by name or email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={() => {
                setPicking(false);
                setQuery("");
              }}
              className="-mr-1 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-44 overflow-y-auto p-1">
            {searching ? (
              <p className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching...
              </p>
            ) : searchError ? (
              <p className="px-2 py-1.5 text-sm text-destructive">
                {searchError}
              </p>
            ) : results.length === 0 ? (
              <p className="px-2 py-1.5 text-sm text-muted-foreground">
                No matching teacher accounts in this organization.
              </p>
            ) : (
              results.map((t) => (
                <button
                  key={t.teacher_id}
                  type="button"
                  onClick={() => addCoTeacher(t)}
                  className="flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-accent"
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-lime-600/10 text-xs font-medium text-lime-900">
                    {(t.name ?? t.email ?? "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {t.name ?? "(no name)"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t.email}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          Add co-teacher
        </button>
      )}
    </div>
  );
};
